
import { Button } from "@/components/ui/button";
import { EnergyBillSection } from './EnergyBillSection';
import { PropertyDetails } from './PropertyDetails';
import { OtherDetails } from './OtherDetails';
import { AdditionalInfoSection } from './AdditionalInfoSection';
import { FormData } from '@/types/form';
import { useState } from 'react';

interface SubmissionFormProps {
  formData: FormData;
  uploadedFiles: {
    gas: string | null;
    electricity: string | null;
    water: string | null;
  };
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSelectChange: (value: string, name: string) => void;
  handleFileUpload: (file: File, type: 'gas' | 'electricity' | 'water') => Promise<string | undefined>;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
}

export const SubmissionForm = ({
  formData,
  uploadedFiles,
  handleInputChange,
  handleSelectChange,
  handleFileUpload,
  handleSubmit
}: SubmissionFormProps) => {
  const [isDualTariff, setIsDualTariff] = useState(false);

  const handleDualTariffChange = (pressed: boolean) => {
    setIsDualTariff(pressed);
    if (pressed) {
      // Copy gas bill details to electricity bill details
      handleSelectChange(formData.gasPlanName, 'electricityPlanName');
      handleInputChange({
        target: {
          name: 'electricityStartDate',
          value: formData.gasStartDate
        }
      } as React.ChangeEvent<HTMLInputElement>);
      handleInputChange({
        target: {
          name: 'electricityEndDate',
          value: formData.gasEndDate
        }
      } as React.ChangeEvent<HTMLInputElement>);
      handleSelectChange(formData.gasPaymentSchedule, 'electricityPaymentSchedule');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="space-y-6">
        <h2 className="text-2xl font-semibold text-[#1F2937]">Submit Your Energy Data</h2>
        
        <PropertyDetails 
          formData={formData}
          handleInputChange={handleInputChange}
          handleSelectChange={handleSelectChange}
          required={true}
        />

        <EnergyBillSection
          title="Gas Bill Details"
          emoji="⚡"
          emojiColor="#F97316"
          type="gas"
          formData={formData}
          handleInputChange={handleInputChange}
          handleSelectChange={handleSelectChange}
          handleFileUpload={handleFileUpload}
          required={true}
          uploadedFiles={uploadedFiles}
        />

        <EnergyBillSection
          title="Electricity Bill Details"
          emoji="⚡"
          emojiColor="#F59E0B"
          type="electricity"
          formData={formData}
          handleInputChange={handleInputChange}
          handleSelectChange={handleSelectChange}
          handleFileUpload={handleFileUpload}
          required={true}
          uploadedFiles={uploadedFiles}
          isDualTariff={isDualTariff}
          onDualTariffChange={handleDualTariffChange}
        />

        <EnergyBillSection
          title="Water Bill Details"
          emoji="💧"
          emojiColor="#50C0FF"
          type="water"
          formData={formData}
          handleInputChange={handleInputChange}
          handleSelectChange={handleSelectChange}
          handleFileUpload={handleFileUpload}
          required={true}
          uploadedFiles={uploadedFiles}
        />

        <OtherDetails
          formData={formData}
          handleInputChange={handleInputChange}
          handleSelectChange={handleSelectChange}
          required={true}
        />

        <AdditionalInfoSection
          formData={formData}
          handleInputChange={handleInputChange}
          handleSelectChange={handleSelectChange}
        />
      </div>

      <div className="flex justify-center">
        <Button
          type="submit"
          className="w-full max-w-md bg-[#059669] hover:bg-[#047857] text-white font-semibold py-4"
        >
          Generate Your Free Insights
        </Button>
      </div>
    </form>
  );
};
